import React, { useEffect, useState, useCallback } from "react";
import { SortableContainer } from "react-sortable-hoc";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

// Local imports
import { MyTableWrapper } from "../style/Style";
import { SortableItem } from "./BrokerRow";
import arrayMove from "../../../core/utils/draggableRowfunc/arrayMove";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError } from "../../../core/utils";

// Shared components
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";

// Constants
const LOADER_COLOR = "#637df4";
const LOADER_SIZE = 150;
const TOAST_POSITION = toast.POSITION.TOP_RIGHT;

const SortableCont = SortableContainer(({ children }) => {
    return <tbody>{children}</tbody>;
});

/**
 * Fetches brokers list from API
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {string} params.type - Broker type
 * @returns {Promise} API response
 */
const getBrokers = async ({ page, type }) => {
    try {
        let url = APP_URLS.BROKER_MANAGEMENT + `?page=${page}&per_page=10`;

        // Add type filter if provided
        if (type) {
            url += `&filter[vertical]=${type}`;
        }

        const response = await API.get(url);

        const resp = response.data;
        if (resp.success === true) {
            return {
                success: true,
                data: resp.data.items,
                total: resp.data.total,
                lastPage: resp.data.last_page,
            };
        } else {
            toast.error(resp.message, {
                position: TOAST_POSITION,
            });
            return {
                success: false,
                error: resp.message,
            };
        }
    } catch (error) {
        const errorMessage = error?.response ? APIError(error) : error?.message || "Failed to fetch brokers";
        toast.error(errorMessage, {
            position: TOAST_POSITION,
        });
        return {
            success: false,
            error: errorMessage,
        };
    }
};

/**
 * Updates broker priority order
 * @param {Array} items - Reordered items array
 * @param {string} type - Broker type (vertical)
 * @returns {Promise} API response
 */
const updateBrokerPriority = async (items, type) => {
    try {
        // Extract broker IDs in the order they appear
        const brokerIds = items.map((element) => element.id);

        const url = APP_URLS.BROKER_MANAGEMENT + `/sort_order`;

        const response = await API.post(url, {
            vertical: type,
            broker_ids: brokerIds,
        });

        const resp = response.data;
        if (resp.success === true) {
            toast.success("Broker Priority Updated Successfully.", {
                position: TOAST_POSITION,
            });
            return {
                success: true,
                data: items,
            };
        } else {
            toast.error(resp.message, {
                position: TOAST_POSITION,
            });
            return {
                success: false,
                error: resp.message,
            };
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.message || APIError(error) || "Failed to update priority";
        toast.error(errorMessage, {
            position: TOAST_POSITION,
        });
        return {
            success: false,
            error: errorMessage,
        };
    }
};

const BrokerTable = ({
    type,
    onEdit,
    refreshTrigger,
    canDeleteBroker,
    canEditBroker,
}) => {

    // Data state
    const [data, setData] = useState([]);
    const [items, setItems] = useState([]);

    // Pagination state
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPage, setTotalPage] = useState(0);
    const [dataDeleted, setDataDeleted] = useState(false);

    // UI state
    const [loaded, setLoaded] = useState(false);
    const [actionloader, setActionloader] = useState(false);

    /**
     * Fetches brokers data from API
     */
    const fetchBrokers = useCallback(async () => {
        setLoaded(true);

        const result = await getBrokers({
            page,
            type,
        });

        if (result.success) {
            setData(result.data);
            setTotal(result.total);
            setTotalPage(result.lastPage);
        }

        setLoaded(false);
    }, [page, type]);

    /**
     * Handles delete broker action
     */
    const handleDelete = useCallback((brokerId) => {
        if (actionloader) return;
        confirmAlert({
            title: `Confirm to delete`,
            message: `Are you sure you want to delete this broker?`,
            closeOnEscape: true,
            closeOnClickOutside: true,
            overlayClassName: "overlay-custom-class-name",
            buttons: [
                {
                    label: "Yes",
                    onClick: () => {
                        setActionloader(true);
                        API.delete(`${APP_URLS.BROKER_MANAGEMENT}/${brokerId}`)
                            .then((res) => {
                                const resp = res.data;
                                if (resp.success === true) {
                                    toast.success("Broker deleted successfully", {
                                        position: TOAST_POSITION,
                                    });
                                    setDataDeleted(true);
                                    fetchBrokers();
                                } else {
                                    toast.error(resp.message, {
                                        position: TOAST_POSITION,
                                    });
                                }
                            })
                            .catch(function (error) {
                                const error_message = APIError(error);
                                toast.error(error_message, {
                                    position: TOAST_POSITION,
                                });
                            })
                            .finally(() => {
                                setActionloader(false);
                            });
                    },
                },
                { label: "No" },
            ],
        });
    }, [actionloader, fetchBrokers]);

    /**
     * Handles drag end event for reordering
     */
    const onSortEnd = useCallback(({ oldIndex, newIndex }) => {
        setItems((oldItems) => {
            const newItems = arrayMove(oldItems, oldIndex, newIndex);
            // Update priority immediately when user drags items
            updateBrokerPriority(newItems, type).then((result) => {
                if (result.success) {
                    setData(result.data);
                }
            });
            return newItems;
        });
    }, [type]);

    // Fetch brokers when page, type, or refresh trigger changes
    useEffect(() => {
        fetchBrokers();
        setDataDeleted(false);
    }, [page, type, refreshTrigger, fetchBrokers, dataDeleted]);

    // Sync items with data (only when data changes from API, not from user drag)
    useEffect(() => {
        if (data.length > 0) {
            setItems(data);
        } else {
            setItems([]);
        }
    }, [data]);

    return (
        <>
            <div className="d-flex align-items-center justify-content-between info-wrap">
                <div className="justify-content-end mt-1 me-2">
                    <span className="text-start">
                        Total Records : <b>{total}</b>
                    </span>
                </div>
            </div>

            <div className="tableContainer">
                <MyTableWrapper>
                    <table className="auctionTable">
                        <thead className="tableHead">
                            <tr className="table-row">
                                <th>Priority</th>
                                <th>Broker Name</th>
                                <th>Status</th>
                                <th>
                                    Lead Quota <br />
                                    <span className="text-muted">
                                        (Daily Used / Daily Quota)
                                        <br />
                                        (Monthly Used / Monthly Quota)
                                    </span>

                                </th>
                                <th>Performance (Last 30 Days)
                                    <span className="text-muted">(Accepted/Rejected)</span>
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        {loaded ? (
                            <div className="d-flex justify-content-center three-dot">
                                <ThreeDots
                                    height={LOADER_SIZE}
                                    width={LOADER_SIZE}
                                    radius="9"
                                    color={LOADER_COLOR}
                                    ariaLabel="three-dots-loading"
                                    wrapperStyle={{}}
                                    wrapperClassName=""
                                    visible={true}
                                />
                            </div>
                        ) : items.length > 0 ? (
                            <SortableCont
                                onSortEnd={onSortEnd}
                                axis="y"
                                lockAxis="y"
                                lockToContainerEdges={true}
                                lockOffset={["30%", "50%"]}
                                helperClass="helperContainerClass"
                                useDragHandle={true}
                            >
                                {items.map((value, index) => (
                                    <SortableItem
                                        key={`item-${value.id || index}`}
                                        index={index}
                                        rowData={value}
                                        onEdit={onEdit}
                                        onDelete={handleDelete}
                                        actionloader={actionloader}
                                        canDeleteBroker={canDeleteBroker}
                                        canEditBroker={canEditBroker}
                                    />
                                ))}
                            </SortableCont>
                        ) : (
                            <tbody>
                                <tr>
                                    <td colSpan="6">
                                        <NoRecord />
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </MyTableWrapper>
            </div>
        </>
    );
};

export default BrokerTable;

